import { css } from '@emotion/css'
import { Badge, Checkbox as MantineCheckbox, Switch, useMantineTheme } from '@mantine/core'
import { Checkbox } from 'components/Checkbox/Checkbox'
import { Select } from 'components/Select/Select'
import { IconDispenser } from 'components/icons/IconDispenser'
import { IconGateway } from 'components/icons/IconGateway'
import { IconRepeater } from 'components/icons/IconRepeater'
import { IconStation } from 'components/icons/IconStation'
import { IconTrap } from 'components/icons/IconTrap'
import { translate } from 'i18n/i18n'
import React, { useEffect } from 'react'
import { DEVICE_TYPE_ICONS, DEVICE_TYPES, getDeviceTypeLabel } from '../Map/_utils/deviceTypes'
import { getEquipmentTypeLabel } from '../Map/_utils/equipmentTypes'
import { getNodeStatusLabel, NODE_STATUS_COLORS } from '../Map/_utils/getActiveNodeStatus'
import type { TEquipmentType } from '../store/serviceCenterStore'
import { serviceCenterStore, type TNodeDeviceType } from '../store/serviceCenterStore'
import type { TNodeStatus } from '../types'

// The doubled class outranks the shared Checkbox wrapper's `.root input` rule.
const whiteCheckboxInputClass = css({
  '&&': {
    backgroundColor: 'white',
    borderColor: 'var(--mantine-color-default-border)',
  },
  '&&:checked': {
    backgroundColor: 'white',
    borderColor: 'white',
  },
})

const Divider: React.FC = () => {
  return (
    <hr
      css={{
        height: '1px',
        borderWidth: 0,
        backgroundColor: 'white',
        opacity: '0.5',
        marginTop: 10,
        marginLeft: 0,
      }}
    />
  )
}

const NODE_TYPE_FILTERS = ['trap', 'gateway', 'dispenser', 'repeater', 'station'] as TEquipmentType[]

const EquipmentTypeIcon: React.FC<{ deviceKey: string }> = ({ deviceKey }) => {
  const style = {
    stroke: '#707070',
    strokeWidth: 3,
  }

  switch (deviceKey) {
    case 'trap':
      return (
        <div style={style}>
          <IconTrap />
        </div>
      )

    case 'gateway':
      return (
        <div style={style}>
          <IconGateway />
        </div>
      )

    case 'dispenser':
      return (
        <div style={style}>
          <IconDispenser />
        </div>
      )

    case 'repeater':
      return (
        <div style={style}>
          <IconRepeater />
        </div>
      )

    case 'station':
      return (
        <div style={style}>
          <IconStation />
        </div>
      )

    default:
      return null
  }
}

const DeviceTypeIcon: React.FC<{ stationKey: (typeof DEVICE_TYPES)[number] }> = ({ stationKey }) => {
  const Icon = DEVICE_TYPE_ICONS[stationKey]

  return <Icon />
}

const STATUS_FILTERS = ['active', 'error', 'planned', 'offline', 'pending'] as TNodeStatus[]

const Header: React.FC<{ title: string; handleClick: () => void; buttonTitle: string }> = ({
  title,
  handleClick,
  buttonTitle,
}) => {
  return (
    <div
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '6px',
        color: 'white',
      }}
    >
      <div style={{ fontSize: '13px' }}>{title}</div>
      <button
        style={{
          background: 'none',
          border: 'none',
          padding: '0',
          margin: '0',
          font: 'inherit',
          color: 'inherit',
          textAlign: 'inherit',
          cursor: 'pointer',
          outline: 'none',
          fontSize: '14px',
          borderBottom: '1px solid white',
          lineHeight: '1px',
        }}
        onClick={handleClick}
      >
        {buttonTitle}
      </button>
    </div>
  )
}

export const EquipmentFilters: React.FC = () => {
  const mapEquipmentTypes = serviceCenterStore.useSelector((s) => s.mapEquipmentTypes)
  const mapEquipmentDevices = serviceCenterStore.useSelector((s) => s.mapEquipmentDevices)
  const mapEquipmentStatuses = serviceCenterStore.useSelector((s) => s.mapEquipmentStatuses)
  const { setMapEquipmentTypes, setMapEquipmentDevices, setMapEquipmentStatuses } = serviceCenterStore.actions
  const isIconDetailBadgeEnabled = serviceCenterStore.useSelector((s) => s.showIconDetailBadge)
  const theme = useMantineTheme()
  const [quickFilterValue, setQuickFilterValue] = React.useState<string | null>(null)

  useEffect(() => {
    switch (quickFilterValue) {
      case 'TRAP_CONSUMABLES': {
        setMapEquipmentTypes(['trap'])

        setMapEquipmentDevices([])

        break
      }

      default:
        return
    }
  }, [quickFilterValue])

  return (
    <>
      <div
        css={{
          zIndex: 100,
          overflowY: 'auto',
        }}
      >
        <div css={{ color: 'white', fontWeight: 'bold', textTransform: 'uppercase' }}>
          {translate.phrases.placeholder('Map Controls')}
        </div>

        <div
          css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        >
          <div style={{ width: '62%', marginTop: '6px', marginRight: '8px' }}>
            <Select
              placeholder="Quick Filter"
              clearable={true}
              data={[
                {
                  label: translate.phrases.placeholder('Trap Consumables'),
                  value: 'TRAP_CONSUMABLES',
                },
              ]}
              value={quickFilterValue}
              handleOnChange={setQuickFilterValue}
            />
          </div>
          <Switch
            checked={isIconDetailBadgeEnabled}
            onChange={() => serviceCenterStore.actions.toggleNodeIconDetailBadge()}
            label={
              <span style={{ color: 'white', cursor: 'pointer' }}>
                {translate.phrases.placeholder('Details')}
              </span>
            }
            color={'teal'}
          />
        </div>
        <Divider />

        {/* Node type filter */}
        <Header
          title={translate.phrases.placeholder('Equipment Filters')}
          handleClick={() => {
            setQuickFilterValue(null)

            mapEquipmentTypes.length === 0
              ? setMapEquipmentTypes(NODE_TYPE_FILTERS)
              : setMapEquipmentTypes([])
          }}
          buttonTitle={
            mapEquipmentTypes.length === 0
              ? translate.phrases.placeholder('Select All')
              : translate.phrases.placeholder('Deselect All')
          }
        />
        <MantineCheckbox.Group
          value={mapEquipmentTypes}
          onChange={(value: string[]) => setMapEquipmentTypes(value as TEquipmentType[])}
        >
          <div css={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {NODE_TYPE_FILTERS.map((nodeTypeKey) => (
              <Checkbox
                css={{ width: '50%', marginTop: '14px' }}
                value={nodeTypeKey}
                key={nodeTypeKey}
                color="white"
                iconColor="#000000"
                classNames={{ input: whiteCheckboxInputClass }}
                label={
                  <div css={{ color: 'white', display: 'flex' }}>
                    <span css={{ display: 'inline-block', marginRight: '8px', fontSize: '20px' }}>
                      <EquipmentTypeIcon deviceKey={nodeTypeKey} />
                    </span>
                    {getEquipmentTypeLabel(nodeTypeKey)}
                  </div>
                }
              />
            ))}
          </div>
        </MantineCheckbox.Group>

        <Divider />

        {/* Device filter */}

        {mapEquipmentTypes.includes('station') && (
          <>
            <Header
              title={translate.phrases.placeholder('Station Filters')}
              handleClick={() =>
                mapEquipmentDevices.length === 0
                  ? setMapEquipmentDevices(DEVICE_TYPES)
                  : setMapEquipmentDevices([])
              }
              buttonTitle={mapEquipmentDevices.length === 0 ? 'Select All' : 'Deselect All'}
            />
            <MantineCheckbox.Group
              value={mapEquipmentDevices}
              onChange={(e: string[]) => {
                setMapEquipmentDevices(e as TNodeDeviceType[])
              }}
            >
              <div css={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                {DEVICE_TYPES.map((stationKey) => (
                  <Checkbox
                    css={{ width: '50%', marginTop: '14px' }}
                    value={stationKey}
                    key={stationKey}
                    color="white"
                    iconColor="#000000"
                    classNames={{ input: whiteCheckboxInputClass }}
                    label={
                      <div css={{ color: 'white', display: 'flex' }}>
                        <span css={{ display: 'inline-block', marginRight: '8px', fontSize: '20px' }}>
                          <DeviceTypeIcon stationKey={stationKey} />
                        </span>
                        {getDeviceTypeLabel(stationKey)}
                      </div>
                    }
                  />
                ))}
              </div>
            </MantineCheckbox.Group>
            <Divider />
          </>
        )}

        {/* Node status filter */}
        <Header
          title={translate.phrases.placeholder('Status Filters')}
          handleClick={() => setMapEquipmentStatuses(mapEquipmentStatuses.length === 0 ? STATUS_FILTERS : [])}
          buttonTitle={mapEquipmentStatuses.length === 0 ? 'Select All' : 'Deselect All'}
        />
        <MantineCheckbox.Group
          value={mapEquipmentStatuses}
          onChange={(e: string[]) => {
            setMapEquipmentStatuses(e as TNodeStatus[])
          }}
        >
          <div css={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((statusKey) => (
              <Checkbox
                css={{ width: '50%', marginTop: '14px' }}
                value={statusKey}
                key={statusKey}
                color="white"
                iconColor="#000000"
                classNames={{ input: whiteCheckboxInputClass }}
                label={
                  <div css={{ color: 'white', display: 'flex' }}>
                    <Badge
                      css={{
                        color: theme.colors.midnight,
                        backgroundColor: NODE_STATUS_COLORS[statusKey as keyof typeof NODE_STATUS_COLORS],
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'none',
                      }}
                    >
                      {getNodeStatusLabel(statusKey)}
                    </Badge>
                  </div>
                }
              />
            ))}
          </div>
        </MantineCheckbox.Group>
      </div>
    </>
  )
}
